import React, { useState, useEffect, useCallback } from 'react';
import {
  XMarkIcon,
  DocumentArrowDownIcon,
  ExclamationTriangleIcon,
  ShieldExclamationIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  ClockIcon,
  GlobeAltIcon,
  BugAntIcon,
  DocumentTextIcon,
  CloudIcon,
  CodeBracketIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import {
  domainInvestigationService,
  Investigation,
  InvestigationReport,
  SecurityEvent,
} from '../services/domainInvestigation';
import { CHART_COLORS } from '../utils/chartColors';

type TabKey = 'overview' | 'events' | 'subdomains' | 'recommendations';

const severityIcon = (severity: string) => {
  const icons: Record<string, JSX.Element> = {
    critical: <ShieldExclamationIcon className='h-5 w-5 text-red-600' />,
    high: <ExclamationTriangleIcon className='h-5 w-5 text-orange-600' />,
    medium: <InformationCircleIcon className='h-5 w-5 text-yellow-600' />,
    low: <CheckCircleIcon className='h-5 w-5 text-green-600' />,
  };
  return icons[severity] ?? <InformationCircleIcon className='h-5 w-5 text-gray-600' />;
};

const severityColor = (severity: string) => {
  const colors: Record<string, string> = {
    critical: 'bg-red-100 text-red-800 border-red-200',
    high: 'bg-orange-100 text-orange-800 border-orange-200',
    medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    low: 'bg-green-100 text-green-800 border-green-200',
  };
  return colors[severity] ?? 'bg-gray-100 text-gray-800 border-gray-200';
};

const riskLevelColor = (riskLevel: string): string => {
  const map: Record<string, string> = {
    critical: CHART_COLORS.danger,
    high: CHART_COLORS.danger,
    medium: CHART_COLORS.warning,
    low: CHART_COLORS.success,
  };
  return map[riskLevel] ?? CHART_COLORS.gray;
};

const eventTypeIcon = (type: string) => {
  const icons: Record<string, JSX.Element> = {
    exposed_buckets: <CloudIcon className='h-4 w-4' />,
    code_leaks: <CodeBracketIcon className='h-4 w-4' />,
    paste_leaks: <DocumentTextIcon className='h-4 w-4' />,
    vulnerabilities: <BugAntIcon className='h-4 w-4' />,
    breach_data: <ShieldExclamationIcon className='h-4 w-4' />,
    phishing_detected: <ExclamationCircleIcon className='h-4 w-4' />,
    phishing_events: <ExclamationCircleIcon className='h-4 w-4' />,
  };
  return icons[type] ?? <InformationCircleIcon className='h-4 w-4' />;
};

const TabsNav = ({ activeTab, setActiveTab }: { activeTab: TabKey; setActiveTab: (t: TabKey) => void }) => (
  <div className='border-b border-gray-200'>
    <nav className='-mb-px flex space-x-8 px-6'>
      {[
        { id: 'overview', name: 'Visão Geral', icon: InformationCircleIcon },
        { id: 'events', name: 'Eventos', icon: BugAntIcon },
        { id: 'subdomains', name: 'Subdomínios', icon: GlobeAltIcon },
        { id: 'recommendations', name: 'Recomendações', icon: CheckCircleIcon },
      ].map(tab => (
        <button
          key={tab.id}
          onClick={() => setActiveTab(tab.id as TabKey)}
          className={`${
            activeTab === tab.id
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
        >
          <tab.icon className='h-4 w-4' />
          <span>{tab.name}</span>
        </button>
      ))}
    </nav>
  </div>
);

const Content = ({
  loading,
  status,
  report,
  activeTab,
  selectedEventType,
  setSelectedEventType,
}: {
  loading: boolean;
  status: string;
  report: InvestigationReport | null;
  activeTab: TabKey;
  selectedEventType: string;
  setSelectedEventType: (t: string) => void;
}) => {
  if (loading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600'></div>
        <span className='ml-2 text-gray-600'>Carregando relatório...</span>
      </div>
    );
  }
  if (status !== 'completed') {
    return (
      <div className='text-center py-12'>
        <ClockIcon className='mx-auto h-12 w-12 text-gray-400' />
        <h3 className='mt-2 text-sm font-medium text-gray-900 dark:text-white'>Investigação em andamento</h3>
        <p className='mt-1 text-sm text-gray-500'>
          O relatório detalhado estará disponível quando a investigação for concluída.
        </p>
        <div className='mt-4'>
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
              status === 'running'
                ? 'bg-blue-100 text-blue-800'
                : status === 'failed'
                  ? 'bg-red-100 text-red-800'
                  : 'bg-yellow-100 text-yellow-800'
            }`}
          >
            {status}
          </span>
        </div>
      </div>
    );
  }
  if (!report) {
    return (
      <div className='text-center py-12'>
        <ExclamationTriangleIcon className='mx-auto h-12 w-12 text-red-400' />
        <h3 className='mt-2 text-sm font-medium text-gray-900 dark:text-white'>Erro ao carregar relatório</h3>
        <p className='mt-1 text-sm text-gray-500'>Não foi possível carregar o relatório da investigação.</p>
      </div>
    );
  }

  const allEvents: SecurityEvent[] = [
    ...report.detailed_findings.exposed_buckets,
    ...report.detailed_findings.code_leaks,
    ...report.detailed_findings.paste_leaks,
    ...report.detailed_findings.vulnerabilities,
    ...report.detailed_findings.breach_data,
    ...(report.detailed_findings.phishing_events || []),
  ];
  const filteredEvents =
    selectedEventType === 'all' ? allEvents : allEvents.filter(event => event.type === selectedEventType);

  if (activeTab === 'overview') {
    return (
      <div className='space-y-6'>
        <div className='bg-gray-50 rounded-lg p-6'>
          <h4 className='text-lg font-medium text-gray-900 dark:text-white mb-4'>Resumo Executivo</h4>
          <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
            <div className='text-center'>
              <div className='text-2xl font-bold text-gray-900 dark:text-white'>
                {report.executive_summary.total_findings}
              </div>
              <div className='text-sm text-gray-500'>Total de Achados</div>
            </div>
            <div className='text-center'>
              <div className='text-2xl font-bold text-white px-3 py-1 rounded' style={{ backgroundColor: riskLevelColor(report.executive_summary.risk_level) }}>
                {report.executive_summary.risk_level.toUpperCase()}
              </div>
              <div className='text-sm text-gray-500'>Nível de Risco</div>
            </div>
            <div className='text-center'>
              <div className='text-2xl font-bold text-red-600'>
                {report.executive_summary.critical_issues}
              </div>
              <div className='text-sm text-gray-500'>Críticos</div>
            </div>
            <div className='text-center'>
              <div className='text-2xl font-bold text-orange-600'>
                {report.executive_summary.high_issues}
              </div>
              <div className='text-sm text-gray-500'>Altos</div>
            </div>
          </div>
          <div className='mt-4 text-sm text-gray-600'>
            <p>
              <strong>Tempo de execução:</strong> {report.executive_summary.execution_time}
            </p>
            <p>
              <strong>Data da investigação:</strong> {new Date(report.executive_summary.investigation_date).toLocaleString('pt-BR')}
            </p>
          </div>
        </div>

        <div>
          <h4 className='text-lg font-medium text-gray-900 dark:text-white mb-4'>Achados de Segurança</h4>
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
            {[
              { key: 'exposed_buckets', label: 'Buckets Expostos', icon: CloudIcon, color: 'text-red-600' },
              { key: 'code_leaks', label: 'Vazamentos de Código', icon: CodeBracketIcon, color: 'text-orange-600' },
              { key: 'paste_leaks', label: 'Vazamentos em Pastes', icon: DocumentTextIcon, color: 'text-yellow-600' },
              { key: 'vulnerabilities', label: 'Vulnerabilidades', icon: BugAntIcon, color: 'text-purple-600' },
              { key: 'data_breaches', label: 'Vazamentos de Dados', icon: ShieldExclamationIcon, color: 'text-red-600' },
              { key: 'phishing_events', label: 'Eventos de Phishing', icon: ExclamationCircleIcon, color: 'text-pink-600' },
            ].map(finding => (
              <div key={finding.key} className='bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4'>
                <div className='flex items-center space-x-3'>
                  <finding.icon className={`h-6 w-6 ${finding.color}`} />
                  <div>
                    <div className='text-lg font-semibold text-gray-900 dark:text-white'>
                      {report.security_findings[finding.key as keyof typeof report.security_findings]}
                    </div>
                    <div className='text-sm text-gray-500'>{finding.label}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (activeTab === 'events') {
    return (
      <div className='space-y-4'>
        <div className='flex flex-wrap gap-2'>
          <button
            onClick={() => setSelectedEventType('all')}
            className={`px-3 py-1 rounded-full text-sm font-medium ${
              selectedEventType === 'all' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Todos ({allEvents.length})
          </button>
          {[
            { key: 'exposed_buckets', label: 'Buckets' },
            { key: 'code_leaks', label: 'Código' },
            { key: 'paste_leaks', label: 'Pastes' },
            { key: 'vulnerabilities', label: 'Vulnerabilidades' },
            { key: 'breach_data', label: 'Vazamentos' },
            { key: 'phishing_events', label: 'Phishing' },
          ].map(type => {
            const eventArray = report.detailed_findings[type.key as keyof typeof report.detailed_findings];
            const count = Array.isArray(eventArray) ? eventArray.length : 0;
            return (
              <button
                key={type.key}
                onClick={() => setSelectedEventType(type.key)}
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  selectedEventType === type.key ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {type.label} ({count})
              </button>
            );
          })}
        </div>

        <div className='space-y-3'>
          {filteredEvents.map((event, index) => (
            <div key={`event-${index}-${event.title}`} className='border border-gray-200 rounded-lg p-4'>
              <div className='flex items-start justify-between'>
                <div className='flex items-start space-x-3'>
                  {eventTypeIcon(event.type)}
                  <div className='flex-1'>
                    <h5 className='font-medium text-gray-900 dark:text-white'>{event.title}</h5>
                    <p className='text-sm text-gray-600 mt-1'>{event.description}</p>
                    <div className='flex items-center space-x-4 mt-2'>
                      <span className='text-xs text-gray-500'>Fonte: {event.source}</span>
                      {event.url && (
                        <a href={event.url} target='_blank' rel='noopener noreferrer' className='text-xs text-blue-600 hover:text-blue-800'>
                          Ver detalhes
                        </a>
                      )}
                    </div>
                  </div>
                </div>
                <div className='flex items-center space-x-2'>
                  {severityIcon(event.severity)}
                  <span className={`px-2 py-1 rounded-full text-xs font-medium border ${severityColor(event.severity)}`}>
                    {event.severity}
                  </span>
                </div>
              </div>
            </div>
          ))}
          {filteredEvents.length === 0 && (
            <div className='text-center py-8'>
              <InformationCircleIcon className='mx-auto h-12 w-12 text-gray-400' />
              <h3 className='mt-2 text-sm font-medium text-gray-900 dark:text-white'>
                Nenhum evento encontrado
              </h3>
              <p className='mt-1 text-sm text-gray-500'>
                Não foram encontrados eventos para o filtro selecionado.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (activeTab === 'subdomains') {
    return (
      <div className='space-y-4'>
        <div className='flex items-center justify-between'>
          <h4 className='text-lg font-medium text-gray-900 dark:text-white'>
            Subdomínios Descobertos ({report.infrastructure_mapping.subdomains_found})
          </h4>
        </div>
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2'>
          {report.infrastructure_mapping.subdomains.map((subdomain, index) => (
            <div key={`subdomain-${subdomain}-${index}`} className='bg-gray-50 rounded-lg p-3'>
              <div className='flex items-center space-x-2'>
                <GlobeAltIcon className='h-4 w-4 text-gray-500' />
                <span className='text-sm font-mono text-gray-900 dark:text-white'>{subdomain}</span>
              </div>
            </div>
          ))}
        </div>
        {report.infrastructure_mapping.subdomains.length === 0 && (
          <div className='text-center py-8'>
            <GlobeAltIcon className='mx-auto h-12 w-12 text-gray-400' />
            <h3 className='mt-2 text-sm font-medium text-gray-900 dark:text-white'>
              Nenhum subdomínio encontrado
            </h3>
            <p className='mt-1 text-sm text-gray-500'>
              A investigação não encontrou subdomínios adicionais.
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      <h4 className='text-lg font-medium text-gray-900 dark:text-white'>Recomendações de Segurança</h4>
      <div className='space-y-3'>
        {report.recommendations.map((recommendation, index) => (
          <div key={`recommendation-${index}-${recommendation.substring(0, 20)}`} className='flex items-start space-x-3 p-4 bg-blue-50 rounded-lg'>
            <CheckCircleIcon className='h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0' />
            <p className='text-sm text-gray-700'>{recommendation}</p>
          </div>
        ))}
      </div>
    </div>
  );
};


interface InvestigationDetailsModalProps {
  investigation: Investigation;
  isOpen: boolean;
  onClose: () => void;
}

const InvestigationDetailsModal: React.FC<InvestigationDetailsModalProps> = ({
  investigation,
  isOpen,
  onClose
}) => {
  const [report, setReport] = useState<InvestigationReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const [selectedEventType, setSelectedEventType] = useState<string>('all');

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const reportData = await domainInvestigationService.getInvestigationReport(investigation.investigation_id);
      setReport(reportData);
    } catch (error) {
      console.error('Erro ao carregar relatório:', error);
      console.error('Erro ao carregar relatório da investigação');
    } finally {
      setLoading(false);
    }
  }, [investigation.investigation_id]);

  useEffect(() => {
    if (isOpen && investigation.status === 'completed') {
      loadReport();
    }
  }, [isOpen, investigation.status, loadReport]);

  const downloadReport = async (format: 'json' | 'pdf' = 'json') => {
    try {
      if (!report) return;

      const dataStr = JSON.stringify(report, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `investigation_${investigation.investigation_id}_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      console.log('Relatório baixado com sucesso!');
    } catch (error) {
      console.error('Erro ao baixar relatório:', error);
      console.error('Erro ao baixar relatório');
    }
  };

  const getAllEvents = (): SecurityEvent[] => {
    if (!report) return [];
    const allEvents: SecurityEvent[] = [
      ...report.detailed_findings.exposed_buckets,
      ...report.detailed_findings.code_leaks,
      ...report.detailed_findings.paste_leaks,
      ...report.detailed_findings.vulnerabilities,
      ...report.detailed_findings.breach_data,
      ...(report.detailed_findings.phishing_events || []),
    ];
    return selectedEventType === 'all' ? allEvents : allEvents.filter(event => event.type === selectedEventType);
  };

  if (!isOpen) return null;

  return (
    <div className='fixed inset-0 z-50 overflow-y-auto'>
      <div className='flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0'>
        <div className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity' onClick={onClose}></div>

        <div className='inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full'>
          {/* Header */}
          <div className='bg-white dark:bg-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700'>
            <div className='flex items-center justify-between'>
              <div>
                <h3 className='text-lg font-medium text-gray-900 dark:text-white'>Detalhes da Investigação</h3>
                <p className='text-sm text-gray-500'>
                  {investigation.domain} • ID: {investigation.investigation_id}
                </p>
              </div>
              <div className='flex items-center space-x-2'>
                {investigation.status === 'completed' && (
                  <button
                    onClick={() => downloadReport('json')}
                    className='inline-flex items-center space-x-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
                  >
                    <DocumentArrowDownIcon className='h-4 w-4' />
                    <span>Baixar Relatório</span>
                  </button>
                )}
                <button onClick={onClose} className='text-gray-400 hover:text-gray-600'>
                  <XMarkIcon className='h-6 w-6' />
                </button>
              </div>
            </div>
          </div>

          <TabsNav activeTab={activeTab} setActiveTab={setActiveTab} />

          {/* Content */}
          <div className='px-6 py-6 max-h-96 overflow-y-auto'>
            <Content
              loading={loading}
              status={investigation.status}
              report={report}
              activeTab={activeTab}
              selectedEventType={selectedEventType}
              setSelectedEventType={setSelectedEventType}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvestigationDetailsModal;
